import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, map } from 'rxjs';
import {
  ApiResponse,
  Group,
  HistoryAction,
  PageableDto,
  PageResponse,
} from '@shared-models';
import { API_CONFIG, ApiConfig } from '../api-config';
import {
  mapPageableContent,
  mapPageableResponse,
} from '../../utils/map-pageable';

/** @deprecated Usar `PageResponse<Group>` de `@shared-models`. */
export type GroupPageResponse = PageResponse<Group>;

@Injectable({ providedIn: 'root' })
export class GroupService {
  private readonly httpClient = inject(HttpClient);
  private readonly apiConfig = inject<ApiConfig>(API_CONFIG);

  private get apiUrl(): string {
    return `${this.apiConfig.apiUrl}/structure/groups`;
  }

  /**
   * Normaliza el status - mantiene el valor del API tal como viene
   */
  private normalizeStatus(status: string | undefined): 'ACTIVE' | 'INACTIVE' {
    if (!status) return 'ACTIVE';
    return status.toUpperCase() as 'ACTIVE' | 'INACTIVE';
  }

  /**
   * Transforma la respuesta del backend al modelo del frontend
   */
  private mapToGroup(item: any): Group {
    const companyIdRaw =
      item.companyId ?? item.company?.id ?? item.companyID ?? null;
    return {
      id: item.id,
      code: item.code || '',
      name: item.name || '',
      description: item.description || '',
      groupLeader: item.groupLeader ?? false,
      companyId:
        companyIdRaw != null && String(companyIdRaw).trim() !== ''
          ? String(companyIdRaw)
          : null,
      status: this.normalizeStatus(item.status),
      // GroupResponse.image (URL). Fallback legado imageUrl por si llega de un mock.
      image: (item.image ?? item.imageUrl ?? '') as string,
      empresasCount: item.empresasCount ?? 0,
      sucursalesCount: item.sucursalesCount ?? item.agentsCount ?? item.agents ?? 0,
      correspondentCount:
        item.correspondentCount ??
        item.correspondentsCount ??
        item.coorespondents ??
        item.correspondents ??
        0,
      createdAt: item.createdAt || '',
      updatedAt: item.updatedAt || '',
    };
  }

  /**
   * Obtiene todos los grupos (con paginación del backend)
   */
  getAll(
    page = 0,
    size = 20,
    sort = 'createdAt',
    direction: 'asc' | 'desc' = 'desc',
    search?: string,
    status?: string,
  ): Observable<GroupPageResponse> {
    let params = new HttpParams()
      .set('page', page.toString())
      .set('size', size.toString())
      .set('sort', sort)
      .set('direction', direction);

    if (search && search.trim()) {
      params = params.set('search', search.trim());
    }
    if (status && status.trim()) {
      params = params.set('status', status.trim());
    }

    return this.httpClient
      .get<ApiResponse<PageableDto>>(this.apiUrl, { params })
      .pipe(
        map((response) =>
          mapPageableResponse(response, (item) => this.mapToGroup(item), size),
        ),
      );
  }

  /**
   * Obtiene un grupo por ID
   */
  getById(id: string): Observable<Group> {
    return this.httpClient
      .get<ApiResponse<any>>(`${this.apiUrl}/${id}`)
      .pipe(map((response) => this.mapToGroup(response.data)));
  }

  /**
   * Serializa el payload como multipart. El back espera el archivo bajo
   * el campo `image` (MultipartFile). La URL de lectura también viene
   * como `image` en GroupResponse; no se reenvía como string en create/update.
   */
  private toFormData(
    data: Partial<Group>,
    imageFile?: File | null,
  ): FormData {
    const formData = new FormData();
    // Omitir `image` / legado `imageUrl`: la URL no se manda en el PUT/POST;
    // el MultipartFile va aparte con la clave `image`.
    const skipKeys = new Set([
      'image',
      'imageUrl',
      'id',
      'createdAt',
      'updatedAt',
      'empresasCount',
      'sucursalesCount',
      'agentsCount',
      'correspondentCount',
      'correspondentsCount',
    ]);

    Object.entries(data).forEach(([key, value]) => {
      if (skipKeys.has(key)) return;
      if (value === undefined || value === null || value === '') return;
      // Boolean como literal "true"/"false" para multipart
      if (typeof value === 'boolean') {
        formData.append(key, value ? 'true' : 'false');
        return;
      }
      formData.append(key, String(value));
    });

    // Si no es jefe, no enviar companyId (aunque viniera vacío en el form)
    if (!data.groupLeader) {
      formData.delete('companyId');
    }

    if (imageFile) {
      formData.append('image', imageFile);
    }

    return formData;
  }

  /**
   * Crea un nuevo grupo
   */
  create(data: Partial<Group>, imageFile?: File | null): Observable<Group> {
    return this.httpClient
      .post<ApiResponse<any>>(this.apiUrl, this.toFormData(data, imageFile))
      .pipe(map((response) => this.mapToGroup(response.data)));
  }

  /**
   * Actualiza un grupo existente
   */
  update(
    id: string,
    data: Partial<Group>,
    imageFile?: File | null,
  ): Observable<Group> {
    return this.httpClient
      .put<ApiResponse<any>>(
        `${this.apiUrl}/${id}`,
        this.toFormData(data, imageFile),
      )
      .pipe(map((response) => this.mapToGroup(response.data)));
  }

  /**
   * Elimina un grupo
   */
  delete(id: string): Observable<{ message: string }> {
    return this.httpClient
      .delete<{ success: boolean; message: string }>(`${this.apiUrl}/${id}`)
      .pipe(
        map((response) => ({
          message: response.message || 'Grupo eliminado exitosamente',
        })),
      );
  }

  /**
   * Actualiza el status de un grupo
   */
  updateStatus(
    id: string,
    status: 'ACTIVE' | 'INACTIVE',
  ): Observable<{ message: string }> {
    return this.httpClient
      .patch<{
        success: boolean;
        message: string;
      }>(`${this.apiUrl}/${id}/status`, { status })
      .pipe(
        map((response) => ({
          message: response.message || 'Status actualizado exitosamente',
        })),
      );
  }

  /**
   * Exporta la lista de grupos a PDF
   */
  exportPdf(options?: {
    title?: string;
    status?: string;
    search?: string;
  }): Observable<Blob> {
    const params = new HttpParams()
      .set('title', options?.title || 'Reporte de grupos')
      .set('status', options?.status || '')
      .set('search', options?.search || '');

    return this.httpClient.get(`${this.apiConfig.apiUrl}/export/pdf/group`, {
      params,
      responseType: 'blob',
    });
  }

  /**
   * Exporta la lista de grupos a Excel
   */
  exportExcel(options?: {
    title?: string;
    status?: string;
    search?: string;
  }): Observable<Blob> {
    const params = new HttpParams()
      .set('title', options?.title || 'Reporte de grupos')
      .set('status', options?.status || '')
      .set('search', options?.search || '');

    return this.httpClient.get(`${this.apiConfig.apiUrl}/export/excel/group`, {
      params,
      responseType: 'blob',
    });
  }

  /**
   * Obtiene el historial de acciones de un grupo
   */
  getHistory(
    id: string,
    page = 0,
    size = 20,
    sort = 'createdAt',
    direction: 'asc' | 'desc' = 'desc',
  ): Observable<HistoryAction[]> {
    const params = new HttpParams()
      .set('page', page.toString())
      .set('size', size.toString())
      .set('sort', sort)
      .set('direction', direction);

    return this.httpClient
      .get<ApiResponse<PageableDto<HistoryAction>>>(
        `${this.apiUrl}/${id}/history`,
        { params },
      )
      .pipe(map((response) => mapPageableContent(response)));
  }

  /**
   * Exporta el historial de un grupo a PDF
   * GET /api/export/pdf/history/group/{id}?title=...
   */
  exportHistoryPdf(
    id: string,
    options?: {
      title?: string;
    },
  ): Observable<Blob> {
    const params = new HttpParams().set(
      'title',
      options?.title || 'Historial de grupos',
    );

    return this.httpClient.get(
      `${this.apiConfig.apiUrl}/export/pdf/history/group/${id}`,
      {
        params,
        responseType: 'blob',
      },
    );
  }

  /**
   * Exporta el historial de un grupo a Excel
   * GET /api/export/excel/history/group/{id}?title=...
   */
  exportHistoryExcel(
    id: string,
    options?: {
      title?: string;
    },
  ): Observable<Blob> {
    const params = new HttpParams().set(
      'title',
      options?.title || 'Historial de grupos',
    );

    return this.httpClient.get(
      `${this.apiConfig.apiUrl}/export/excel/history/group/${id}`,
      {
        params,
        responseType: 'blob',
      },
    );
  }
}
